import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  api,
  type BenchmarkRow,
  type BenchmarkSlice,
  type MarketBenchmark,
  type PairedDifference,
} from '../../api'
import { marketLabel } from '../../markets'
import { BookmakerPicker } from '../BookmakerPicker'
import { Detail } from '../Detail'
import { Term } from '../Term'
import { DataTable, type Column } from '../ui/DataTable'
import { Tabs } from '../ui/Tabs'
import { date, percent, signed } from './format'
import { PanelHeading } from './parts'

/**
 * Our published probabilities against a bookmaker's, on the same events, with the results.
 *
 * **The page this site was built to earn.** Every other measurement here grades the model against
 * what happened. This one grades it against the only forecaster whose numbers are worth beating —
 * on identical fixtures, with proper scoring rules, and with an interval wide enough to be honest
 * about how little a few dozen fixtures can settle.
 *
 * **Two colours and no verdict palette.** Our forecaster is the accent; the bookmaker is neutral
 * ink. That is identity, not judgement — painting "we are ahead" in the outcome green would put a
 * won-bet colour on a statistic that is not an outcome, and would shout a result the interval does
 * not support. The one place colour appears as meaning is the interval bar itself, and it is the
 * accent only when the interval clears zero.
 *
 * **What the page must never do** is let a reader read a lead as a finding. So the sentence at the
 * top is written from the interval rather than from the point estimate, the smallest gap this sample
 * could have detected is printed beside the gap it did measure, and the fixture count — not the
 * market count — is what every claim is qualified by.
 */
export function MarketPanel({ leagueId }: { leagueId?: number }) {
  const [book, setBook] = useState<string | undefined>(undefined)
  const [point, setPoint] = useState<'pre_draft' | 'post_draft'>('pre_draft')

  const benchmark = useQuery({
    queryKey: ['benchmark', leagueId ?? 'all', book ?? 'default', point],
    queryFn: () => api.benchmark(leagueId, book, point),
    refetchInterval: 120_000,
  })

  if (benchmark.isPending) {
    return (
      <div className="space-y-4" aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading…</span>
        <div className="h-11 w-64 max-w-full animate-pulse rounded bg-surface-raised motion-reduce:animate-none" />
        <div className="h-52 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
        <div className="h-40 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
      </div>
    )
  }

  if (benchmark.error) {
    return (
      <p className="rounded border border-danger-edge bg-danger-bg px-4 py-3 text-sm text-danger">
        Could not load the comparison against the market.
      </p>
    )
  }

  const data = benchmark.data

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <p className="max-w-3xl text-sm text-ink-soft">
          A bookmaker is a forecaster too, and a better-resourced one. This grades our published
          number against theirs on the fixtures we hold a price for — same matches, same moment,
          margin stripped out — and reports the difference with an interval, because on a record this
          size most differences are the sample.
        </p>

        {/* The two controls a reader might want, side by side, above everything they change. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <BookmakerPicker
            books={data.availableBookmakers}
            selected={data.bookmaker}
            onSelect={setBook}
          />
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-sm text-ink-soft">Our number</span>
            <Tabs
              ariaLabel="Which of our numbers to compare"
              active={point}
              onChange={setPoint}
              tabs={[
                { key: 'pre_draft', label: 'Before the draft' },
                { key: 'post_draft', label: 'After the draft' },
              ]}
            />
          </div>
        </div>

        {data.counterfactual ? (
          <p className="rounded border border-warn-edge bg-warn-bg px-3 py-2 text-xs text-warn">
            <strong>This is not a fair fight, and it is not meant to be.</strong> Our number here has
            seen the ten heroes; the price mostly has not, because bookmakers rarely reprice between
            the draft and the horn. Any lead is an information advantage rather than a better
            forecaster — which is exactly the opportunity this site was built to catch, and exactly
            why it must not be read as evidence the model is sharper.
          </p>
        ) : null}
      </section>

      {data.maps ? (
        <Verdict slice={data.maps} book={data.bookmakerName} />
      ) : (
        <p className="rounded border border-line bg-surface px-4 py-6 text-sm text-ink-soft">
          {data.bookmakerName} has no settled map market on record yet — {emptyReason(data)}.
        </p>
      )}

      {data.maps ? <Anatomy slice={data.maps} book={data.bookmakerName} /> : null}
      {data.maps ? <Independence slice={data.maps} book={data.bookmakerName} /> : null}
      {data.series ? <Series slice={data.series} book={data.bookmakerName} /> : null}

      {data.rows.length > 0 ? <Rows rows={data.rows} book={data.bookmakerName} /> : null}

      <Method data={data} />
    </div>
  )
}

/** Why there is nothing to show, in the reader's terms rather than as an empty state. */
function emptyReason(data: MarketBenchmark): string {
  const { basis } = data
  if (basis.noPrice > 0 && basis.observations === 0 && basis.notSettled === 0) {
    return 'we hold prices from it, but none on a market that has finished'
  }
  if (basis.notSettled > 0) {
    return `${basis.notSettled} priced ${basis.notSettled === 1 ? 'market is' : 'markets are'} still to be played`
  }
  return 'nothing it priced has both a settled result and a forecast we recorded at the time'
}

/**
 * The headline: two scores, the gap between them, and how much of a gap would even be visible.
 *
 * The sentence is generated from the INTERVAL, never from the point estimate. A lead of four
 * thousandths of Brier over fourteen fixtures is not a lead, and a page that says "we are ahead"
 * whenever the mean is positive is a page that will say it about noise roughly half the time.
 */
function Verdict({ slice, book }: { slice: BenchmarkSlice; book: string }) {
  const { brier, logLoss, model, market } = slice

  return (
    <section className="rounded-lg border border-line bg-surface p-4 sm:p-5">
      <p className="max-w-3xl text-base text-ink">{sentence(slice, book)}</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Side
          who="Our model"
          tone="accent"
          brier={model.brier}
          logLoss={model.logLoss}
          accuracy={model.accuracy}
          dispersion={model.dispersion}
        />
        <Side
          who={book}
          tone="neutral"
          brier={market.brier}
          logLoss={market.logLoss}
          accuracy={market.accuracy}
          dispersion={market.dispersion}
        />
      </div>

      {brier ? (
        <div className="mt-5 space-y-4 border-t border-line pt-4">
          <Interval
            label="Brier"
            difference={brier}
            places={4}
            note={`over ${brier.observations} map ${brier.observations === 1 ? 'market' : 'markets'} on ${brier.clusters} ${brier.clusters === 1 ? 'fixture' : 'fixtures'}`}
          />
          {logLoss ? <Interval label="Log loss" difference={logLoss} places={3} note="the same rows, scored the other way" /> : null}
        </div>
      ) : (
        <p className="mt-4 border-t border-line pt-4 text-sm text-ink-faint">
          One fixture cannot carry an interval, so there is a difference here and no way to say
          whether it means anything.
        </p>
      )}
    </section>
  )
}

/**
 * The verdict in a sentence, and the hardest thing on this page to write honestly.
 *
 * Four states, and only one of them is a finding. The wording separates *which way the point
 * estimate falls* from *whether the record can tell* — because those are different claims and
 * collapsing them is how a betting site ends up advertising a lead it cannot support.
 *
 * Each branch is a whole sentence rather than fragments joined at the seams. The first version of
 * this built the "marginally ahead" line by concatenating a subject, a book name and a preposition,
 * and produced "Pinnacle is marginally ahead of ours on 11 fixtures" — grammar assembled by
 * conditional, which is how prose rots one edit at a time.
 */
function sentence(slice: BenchmarkSlice, book: string): React.ReactNode {
  const { brier, model, market } = slice
  const basis = `${slice.fixtures} ${slice.fixtures === 1 ? 'fixture' : 'fixtures'}`

  if (!brier) {
    return (
      <>
        Our number scored {model.brier.toFixed(4)} against {book}&rsquo;s {market.brier.toFixed(4)}{' '}
        on {basis}. Far too few to say anything about which forecaster is better.
      </>
    )
  }

  const separated = brier.low > 0 || brier.high < 0
  const ahead = brier.mean > 0

  if (separated && ahead) {
    return (
      <>
        <strong>Our numbers have beaten {book} here</strong>, by more than chance produces over{' '}
        {basis}. That is the finding this page exists to look for — and one book at one tournament
        over {basis} is where a finding starts, not where it is settled.
      </>
    )
  }

  if (separated) {
    return (
      <>
        <strong>{book} has beaten our numbers here</strong>, by more than chance produces over{' '}
        {basis}. That is the expected result and the honest one: the price is set by a
        better-informed forecaster, which is the reason no edge on this site should be acted on.
      </>
    )
  }

  if (ahead) {
    return (
      <>
        Our numbers came out marginally better than {book}&rsquo;s over {basis}, and{' '}
        <strong>the gap is well inside what chance produces</strong> at this sample. Nothing here
        separates the two forecasters yet.
      </>
    )
  }

  return (
    <>
      {book}&rsquo;s prices came out marginally better than our numbers over {basis}, and{' '}
      <strong>the gap is well inside what chance produces</strong> at this sample. Nothing here
      separates the two forecasters yet.
    </>
  )
}

/** One forecaster's four numbers, in its own identity colour. */
function Side({
  who,
  tone,
  brier,
  logLoss,
  accuracy,
  dispersion,
}: {
  who: string
  tone: 'accent' | 'neutral'
  brier: number
  logLoss: number
  accuracy: number
  dispersion: number
}) {
  return (
    <div className="rounded border border-line bg-surface-raised p-3">
      <div
        className={`text-xs font-medium uppercase tracking-wide ${
          tone === 'accent' ? 'text-accent' : 'text-ink-faint'
        }`}
      >
        {who}
      </div>
      <div className="tabular mt-1 text-2xl text-ink">{brier.toFixed(4)}</div>
      <div className="mt-0.5 text-xs text-ink-faint">
        <Term id="brier" /> — lower is better
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-line pt-2 text-xs">
        <Pair label="Log loss" value={logLoss.toFixed(3)} />
        <Pair label="Named winner" value={percent(accuracy)} />
        <Pair
          label="From evens"
          value={`${(dispersion * 100).toFixed(1)} pts`}
        />
      </dl>
    </div>
  )
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-ink-faint">{label}</dt>
      <dd className="tabular text-ink-soft">{value}</dd>
    </div>
  )
}

/**
 * The difference and its interval, drawn against zero.
 *
 * **Why a picture rather than three numbers.** "+0.0043, 95% CI [−0.0180, +0.0266]" is complete and
 * unreadable; the thing a reader needs to see is that the bar straddles the line, which a bar
 * straddling a line says instantly.
 *
 * **Three decisions make it honest.** The axis is symmetric about zero, so neither forecaster is
 * given more room than the other. It is scaled to include the *detectable* difference as well as the
 * measured one, so a tiny measured gap cannot zoom the axis until it looks enormous. And the
 * detectable difference is drawn, as a pair of marks — without them the interval is a rail with a
 * dot on it and there is nothing on the chart to judge its width against, which is the one thing the
 * width is for.
 *
 * The zero rule is painted ON TOP of the bar. Underneath it, an interval this wide simply covered
 * it, and the chart's only reference point disappeared exactly when it mattered most.
 *
 * Hand-drawn in percentage-positioned divs, like the reliability chart, for the same reason: it
 * stays responsive with no width measurement, and there is no chart library default here that could
 * quietly start the axis somewhere flattering.
 */
function Interval({
  label,
  difference,
  places,
  note,
}: {
  label: string
  difference: PairedDifference
  places: number
  note: string
}) {
  const separated = difference.low > 0 || difference.high < 0
  const span =
    Math.max(
      Math.abs(difference.low),
      Math.abs(difference.high),
      difference.detectableDifference,
      1e-9,
    ) * 1.2

  const at = (value: number) => ((value + span) / (2 * span)) * 100
  const left = at(Math.min(difference.low, difference.high))
  const right = at(Math.max(difference.low, difference.high))

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-xs">
        <span className="text-ink-soft">
          {label} difference{' '}
          <Detail
            label={`How the ${label.toLowerCase()} difference is measured`}
            heading={<span className="text-ink-faint">how</span>}
          >
            Their score minus ours, market by market, averaged. Positive means our number scored
            better. The interval is clustered on the fixture, so a best-of-three that priced its
            series and three maps counts as one fixture rather than four — those four are nearly the
            same forecast, and counting them separately would make the interval a third too narrow.
          </Detail>
        </span>
        <span className="tabular text-ink">
          {signed(difference.mean, places)}
          <span className="text-ink-faint">
            {' '}
            [{signed(difference.low, places)}, {signed(difference.high, places)}]
          </span>
        </span>
      </div>

      <div className="relative mt-2 h-7">
        <div
          className={`absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full ${
            separated ? 'bg-accent' : 'bg-line-strong'
          }`}
          style={{ left: `${left}%`, width: `${Math.max(right - left, 0.5)}%` }}
        />
        <div
          className={`absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface ${
            separated ? 'bg-accent' : 'bg-ink-faint'
          }`}
          style={{ left: `${at(difference.mean)}%` }}
        />

        {/*
          The two reference marks go on TOP of the bar, not under it.

          Underneath, an interval this wide simply covered them — and covering them removes the only
          two things on the chart the bar's width can be judged against, exactly when the bar is at
          its most misleading. Zero is the darkest mark on the chart because it is the only one that
          decides anything.
        */}
        {[-difference.detectableDifference, difference.detectableDifference].map((mark) => (
          <div
            key={mark}
            className="absolute inset-y-0 w-px bg-line-strong"
            style={{ left: `${at(mark)}%` }}
          />
        ))}
        <div className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-ink" />
      </div>

      <div className="flex items-baseline justify-between text-xs text-ink-faint">
        <span>← the book forecast better</span>
        <span>we forecast better →</span>
      </div>

      <p className="mt-2 text-xs text-ink-faint">
        {note}. The two faint marks are{' '}
        <strong className="tabular text-ink-soft">
          ±{difference.detectableDifference.toFixed(places)}
        </strong>{' '}
        — a true gap smaller than that would be invisible at this many fixtures, whichever way it ran.
      </p>
    </div>
  )
}

/**
 * Where the difference comes from: information, or calibration.
 *
 * **The most useful thing on this page once the headline is read**, because the two have unrelated
 * cures. A forecaster loses by stating percentages that do not happen, which recalibration fixes, or
 * by not committing far enough from the average in the first place, which nothing but better
 * features fixes. This site's own documentation argues the second on theory; here it is measured.
 *
 * **Not a table.** It was one, and on a phone the second column — the bookmaker's, half the
 * comparison — sat outside the viewport behind a horizontal scroll with no hint it existed. A table
 * whose entire purpose is putting two numbers beside each other cannot afford to hide one of them,
 * which is the argument `DataTable` was written for. Each term is a row that stacks its two values
 * side by side at every width instead.
 */
function Anatomy({ slice, book }: { slice: BenchmarkSlice; book: string }) {
  const ours = slice.modelMurphy
  const theirs = slice.marketMurphy

  if (!ours || !theirs) {
    return null
  }

  // A fit that separates the outcomes outright has nothing left to explain, so resolution reaches
  // the uncertainty and the whole decomposition is describing the sample rather than the forecaster.
  const saturated =
    ours.resolution >= ours.uncertainty - 1e-9 || theirs.resolution >= theirs.uncertainty - 1e-9

  return (
    <section>
      <PanelHeading
        lead="A forecast can be worse for two unrelated reasons: it states percentages that do not
          happen, or it never commits far enough from the average to be useful. Only the first is a
          calibration problem, and only the first can be fixed without better information."
      >
        Where the gap comes from
      </PanelHeading>

      <ul className="mt-3 space-y-2">
        <Comparison
          name={<Term id="resolution" />}
          detail="How much better than always saying the average, once the stated percentages are corrected. More is better. This is information, and no recalibration adds any."
          book={book}
          ours={ours.resolution}
          theirs={theirs.resolution}
        />
        <Comparison
          name="Miscalibration"
          detail="What relabelling the percentages would buy, leaving the ordering alone. Less is better. This is the part a recalibration layer can fix — and on this record it is the smaller half of the gap for both forecasters."
          book={book}
          ours={ours.reliability}
          theirs={theirs.reliability}
        />
        <Comparison
          name="Distance from evens"
          detail="How far each forecaster is willing to move from 50%. Not part of the decomposition — it is the visible symptom of the resolution difference above, and the number this site's own standing caveat quotes."
          book={book}
          ours={slice.model.dispersion}
          theirs={slice.market.dispersion}
        />
      </ul>

      {/*
        THE SATURATION WARNING, and it is not decoration.

        Both terms are read off a calibration curve fitted to the very outcomes being scored, so both
        are biased in the flattering direction — an earlier version of this note said only
        miscalibration was, which is half the truth and the wrong half. On a handful of markets the
        forecaster's ordering can separate the wins from the losses outright, the fitted curve scores
        zero, and RESOLUTION lands on the uncertainty exactly. Seen on real data at four markets: two
        distinct levels, resolution 0.2500 against an uncertainty of 0.2500, which reads as a
        spectacular forecaster and is an artefact of the sample being smaller than the fit.

        So the levels each ordering supports are printed. They are what the resolution term rests on,
        and they are the number that makes a saturated fit obvious rather than impressive.
      */}
      {saturated ? (
        <p className="mt-3 rounded border border-warn-edge bg-warn-bg px-3 py-2 text-xs text-warn">
          <strong>Too few markets for this decomposition to mean much.</strong> Each forecaster is
          scored against a calibration curve fitted to these same outcomes, and here the ordering
          separates them outright — {ours.levels} distinct{' '}
          {ours.levels === 1 ? 'level' : 'levels'} for ours and {theirs.levels} for{' '}
          {book}&rsquo;s — so the fit is perfect and resolution lands on the uncertainty by
          construction. It is not a finding. Read it again when the record runs to dozens of markets.
        </p>
      ) : null}

      <p className="mt-3 text-xs text-ink-faint">
        Brier = uncertainty − resolution + miscalibration, exactly, and the uncertainty in these
        games is <strong className="tabular text-ink-soft">{ours.uncertainty.toFixed(4)}</strong> for
        both — being a fact about the matches rather than about anyone&rsquo;s opinion of them. Each
        forecaster is decomposed against its own isotonic recalibration rather than into fixed bins,
        which is what makes the three terms add up to the scores above instead of nearly adding up to
        them. That curve is fitted to the outcomes it is then scored on, so{' '}
        <strong className="text-ink-soft">both</strong> terms are biased in the flattering direction
        — resolution up, miscalibration up — and equally for the two forecasters. Read the difference
        between the columns, never either number on its own. The ordering behind resolution rests on{' '}
        {ours.levels} distinct {ours.levels === 1 ? 'level' : 'levels'} for our numbers and{' '}
        {theirs.levels} for {book}&rsquo;s.
      </p>
    </section>
  )
}

/** One term, with both forecasters' values beside each other at every width. */
function Comparison({
  name,
  detail,
  book,
  ours,
  theirs,
}: {
  name: React.ReactNode
  detail: string
  book: string
  ours: number
  theirs: number
}) {
  return (
    <li className="rounded-lg border border-line bg-surface px-3 py-2 sm:flex sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0 text-sm text-ink">
        <Detail
          label={`What ${typeof name === 'string' ? name.toLowerCase() : 'this term'} means`}
          heading={name}
        >
          {detail}
        </Detail>
      </div>
      {/* Fixed widths from `sm` up so the two numbers line up down the list and can be read as a
          column, rather than drifting with the length of each term's name. */}
      <dl className="mt-1 grid shrink-0 grid-cols-2 gap-4 text-xs sm:mt-0 sm:gap-6">
        <div className="sm:w-24">
          <dt className="text-accent">Our model</dt>
          <dd className="tabular text-base text-ink">{ours.toFixed(4)}</dd>
        </div>
        <div className="sm:w-24">
          <dt className="truncate text-ink-faint">{book}</dt>
          <dd className="tabular text-base text-ink">{theirs.toFixed(4)}</dd>
        </div>
      </dl>
    </li>
  )
}


/**
 * Does our number carry anything the price does not?
 *
 * **A different question from who scores better, and a stronger one.** A model can lose overall and
 * still hold information the market lacks — that is a combination worth having, and it is the only
 * result that would justify publishing an edge at all. It is also the first thing to collapse at
 * small n, so it declines rather than reporting a coefficient nobody should read.
 */
function Independence({ slice, book }: { slice: BenchmarkSlice; book: string }) {
  const fit = slice.encompassing

  return (
    <section>
      <PanelHeading
        lead="The strongest question available: with the bookmaker's own number already accounted for,
          does ours still explain anything? A model can lose on points and still know something the
          price does not — that is the combination worth having."
      >
        Does our number add anything?
      </PanelHeading>

      {fit ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Weight
            who="Our model"
            tone="accent"
            weight={fit.modelWeight}
            says={
              fit.modelWeight.informative
                ? 'carries information the price does not'
                : 'nothing here that the price does not already have'
            }
          />
          <Weight
            who={book}
            tone="neutral"
            weight={fit.marketWeight}
            says={
              fit.marketWeight.informative
                ? 'carries information ours does not'
                : 'nothing here that our number does not already have'
            }
          />
        </div>
      ) : (
        <p className="mt-3 rounded border border-line bg-surface px-4 py-3 text-sm text-ink-soft">
          Not yet. Fitting both forecasts against the results needs three parameters and a sample
          that can carry them — at least 40 settled markets across three fixtures, against{' '}
          {slice.model.count} and {slice.fixtures} here. It converges happily on fewer and reports
          intervals several units wide, which looks like an answer and is not.
        </p>
      )}

      {fit ? (
        <p className="mt-2 text-xs text-ink-faint">
          Both forecasts, as log-odds, fitted against what happened. A weight near 1 with the other
          near 0 means that forecaster is doing all the work. The intervals are clustered on the
          fixture and, on {fit.observations} markets across {fit.clusters} fixtures, they are wide —
          this is the measurement that needs the most data and gives the clearest answer once it has
          it.
        </p>
      ) : null}
    </section>
  )
}

function Weight({
  who,
  tone,
  weight,
  says,
}: {
  who: string
  tone: 'accent' | 'neutral'
  weight: { estimate: number; low: number; high: number; informative: boolean }
  says: string
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div
        className={`text-xs font-medium uppercase tracking-wide ${
          tone === 'accent' ? 'text-accent' : 'text-ink-faint'
        }`}
      >
        {who}
      </div>
      <div className="tabular mt-1 text-xl text-ink">{weight.estimate.toFixed(2)}</div>
      <div className="tabular mt-0.5 text-xs text-ink-faint">
        95% interval {signed(weight.low, 2)} to {signed(weight.high, 2)}
      </div>
      <p className="mt-2 text-xs text-ink-soft">{says}</p>
    </div>
  )
}

/**
 * The series market, kept beside the maps and never pooled with them.
 *
 * The model estimates a single map; its series number is that estimate put through the best-of-three
 * conversion, which amplifies our error exactly as faithfully as it amplifies our edge. The
 * bookmaker prices the series directly. So a difference here is partly about the conversion, and
 * pooling the two slices would blame the model for that or credit it for it.
 */
function Series({ slice, book }: { slice: BenchmarkSlice; book: string }) {
  return (
    <section>
      <PanelHeading
        lead="Kept apart from the maps above on purpose. We do not forecast a series — we forecast one
          map and convert, assuming the maps are independent. So a difference here measures that
          assumption as much as it measures the model."
      >
        Series markets
      </PanelHeading>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Stat label="Our Brier" value={slice.model.brier.toFixed(4)} tone="accent" />
        <Stat label={`${book}'s Brier`} value={slice.market.brier.toFixed(4)} tone="neutral" />
        <Stat
          label="Difference"
          value={slice.brier ? signed(slice.brier.mean, 4) : '—'}
          tone="neutral"
          note={
            slice.brier
              ? `${slice.brier.clusters} fixtures · ${
                  slice.brier.low > 0 || slice.brier.high < 0
                    ? 'beyond what chance produces'
                    : 'inside what chance produces'
                }`
              : 'needs two fixtures'
          }
        />
      </div>
    </section>
  )
}

function Stat({
  label,
  value,
  tone,
  note,
}: {
  label: string
  value: string
  tone: 'accent' | 'neutral'
  note?: string
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div className={`text-xs ${tone === 'accent' ? 'text-accent' : 'text-ink-faint'}`}>{label}</div>
      <div className="tabular mt-1 text-xl text-ink">{value}</div>
      {note ? <div className="mt-0.5 text-xs text-ink-faint">{note}</div> : null}
    </div>
  )
}

/**
 * Every paired forecast, so the summary above can be checked rather than believed.
 *
 * The site's one promise is that it publishes the arithmetic. A page that reports a Brier difference
 * and does not show the rows it came from is asking to be trusted, which is the opposite.
 */
function Rows({ rows, book }: { rows: BenchmarkRow[]; book: string }) {
  const columns: Column<BenchmarkRow>[] = [
    {
      head: 'Fixture',
      card: 'title',
      cell: (row) => (
        <span className="text-ink">
          {row.teamA} <span className="text-ink-faint">v</span> {row.teamB}
        </span>
      ),
    },
    { head: 'Market', cell: (row) => marketLabel(row.market) },
    { head: 'Played', cell: (row) => date(row.startTime) },
    {
      head: 'Ours',
      align: 'right',
      card: 'lead',
      cell: (row) => <span className="text-accent">{percent(row.modelProbability, 1)}</span>,
    },
    {
      head: book,
      align: 'right',
      cell: (row) => percent(row.marketProbability, 1),
    },
    {
      head: 'Team A won',
      align: 'right',
      cell: (row) => (row.teamAWon ? 'yes' : 'no'),
    },
    {
      // THE SAME SIGN CONVENTION AS THE INTERVAL ABOVE, and it was not. This column read
      // "ours − theirs", where lower is better and so a NEGATIVE number was the good one, directly
      // below a chart where positive was the good one — two opposite conventions on one page, for
      // the same quantity, with nothing saying so.
      head: 'Brier gap',
      align: 'right',
      cell: (row) => signed(row.marketBrier - row.modelBrier, 3),
    },
  ]

  return (
    <section>
      <PanelHeading
        lead="Every row the figures above are built from. Probabilities are for the first-named team,
          which is fixed by the schedule long before anyone looks at a price, and the gap is theirs
          minus ours — positive means our number scored better on that market."
      >
        Every paired forecast
      </PanelHeading>
      <div className="mt-3">
        <DataTable columns={columns} rows={rows} keyOf={(row) => `${row.fixtureId}-${row.market}`} />
      </div>
    </section>
  )
}

/** The rules, the exclusions and the assumption behind the market's number. */
function Method({ data }: { data: MarketBenchmark }) {
  const { basis } = data
  const shin = data.maps?.devigSensitivity.find((entry) => entry.method === 'shin')
  const used = data.maps?.devigSensitivity.find((entry) => entry.method === data.devigMethod)
  const flips = shin && used ? Math.sign(shin.brierDifference) !== Math.sign(used.brierDifference) : false

  return (
    <details className="rounded-lg border border-line bg-surface">
      <summary className="cursor-pointer px-4 py-2 text-xs text-ink-faint hover:text-ink-soft">
        How this is measured, and what it leaves out
      </summary>
      <div className="space-y-4 border-t border-line p-4 text-sm text-ink-soft">
        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-ink-faint">
            What is paired
          </h4>
          <ul className="mt-2 space-y-1.5 text-sm">
            <li>
              <strong className="text-ink">A fixed side.</strong> Every figure is for the first-named
              team. Scoring the side our own suggestion picked would select rows precisely because we
              disagree with the market upwards, and a forecaster with no skill would look like it had
              an edge.
            </li>
            <li>
              <strong className="text-ink">Our number as it stood when the price was struck</strong>,
              from the recorded history — not the fixture&rsquo;s current figure, which is rewritten
              whenever the ratings advance or a new model activates.
            </li>
            <li>
              <strong className="text-ink">The same information on both sides.</strong> The default
              compares our pre-draft number against the last price set before the draft was visible.
            </li>
            <li>
              <strong className="text-ink">Never a match the model was trained on.</strong> An
              unknown training cut-off scores nothing at all.
            </li>
            <li>
              <strong className="text-ink">Settled markets only.</strong> A map the series never
              reached is refunded, not lost, so there is no outcome to grade either forecaster on.
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-ink-faint">
            What was left out, and why
          </h4>
          <dl className="mt-2 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <Left label="Priced but not yet played" value={basis.notSettled} />
            <Left label="Maps the series never reached" value={basis.void} />
            <Left label="Settled but never priced by this book" value={basis.noPrice} />
            <Left label="Priced but we had no number at the time" value={basis.noForecast} />
            <Left label="Inside the model's own training window" value={basis.inSample} />
          </dl>
        </div>

        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-ink-faint">
            Whose numbers these are
          </h4>
          <p className="mt-2 text-xs">
            {basis.modelVersions.length === 0
              ? 'No forecasts on record.'
              : basis.modelVersions
                  .map((version) => `${version.version} (${version.count})`)
                  .join(', ')}
            {basis.modelVersions.length > 1
              ? ' — more than one, because these are the numbers the site published at the time and the champion changed during the record. Pooling them answers "what did we say", not "how good is the current model".'
              : '.'}
          </p>
        </div>

        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-ink-faint">
            The bookmaker&rsquo;s margin
          </h4>
          <p className="mt-2 text-xs">
            A price is not a probability: the two sides sum to more than one, and the excess is the
            book&rsquo;s cut. Comparing against the raw implied number would hand us that whole margin
            as imaginary skill. Removing it is an assumption about where it sits, so the headline is
            computed twice — proportionally, which is what every other page here uses, and by
            Shin&rsquo;s method, which loads more of it onto the longshot.
            {used && shin ? (
              <>
                {' '}
                Proportional gives{' '}
                <strong className="tabular text-ink-soft">{signed(used.brierDifference, 4)}</strong>;
                Shin gives{' '}
                <strong className="tabular text-ink-soft">{signed(shin.brierDifference, 4)}</strong>.{' '}
                {flips
                  ? 'They disagree about the SIGN, which means the verdict above is about the de-vig rather than about the two forecasters. Believe neither until that is understood.'
                  : 'They agree about the direction, so the verdict does not rest on the choice.'}
              </>
            ) : null}{' '}
            <Term id="devig" />
          </p>
        </div>

        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-ink-faint">
            Three things this cannot tell you
          </h4>
          <ul className="mt-2 space-y-1.5 text-xs">
            <li>
              <strong className="text-ink">It is not a profit and loss.</strong> Beating a book on
              score is necessary for an edge and nowhere near sufficient — the margin is charged
              whatever either party believes. The bet record is where money is counted.
            </li>
            <li>
              <strong className="text-ink">The fixtures are not a random sample.</strong> A price is
              bought against a hard monthly allowance, so what is here is what capture could afford,
              which is biased towards fixtures the system saw coming.
            </li>
            <li>
              <strong className="text-ink">There are dozens of books to choose from.</strong> Flip
              through enough of them and one will look beaten by chance alone. The interval on a
              single book does not account for having gone looking.
            </li>
          </ul>
        </div>
      </div>
    </details>
  )
}

function Left({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between gap-3 sm:justify-start">
      <dt className="text-ink-faint">{label}</dt>
      <dd className="tabular text-ink-soft">{value}</dd>
    </div>
  )
}
